import {
  AddCircleOutlined,
  CalendarTodayOutlined,
  ContentCopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExpandMoreOutlined,
  RefreshOutlined,
  SendOutlined,
  SwapHorizOutlined,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { useAppController } from 'customHooks/useAppController'
import { useBomItemThumbnailImageSrc } from 'features/BillOfMaterials/useBomItemThumbnailImageSrc'
import { useDialogManager } from 'features/DialogManager/useDialogManager'
import { ProjectSuppliersController } from 'features/ProjectSuppliers/ProjectSuppliersController'
import { ExchangeChannelDialog } from 'features/RequestForQuotations/ExchangeChannelDialog'
import {
  ChannelBadge,
  ExchangeDeliveryDetails,
} from 'features/RequestForQuotations/ExchangeDeliveryDetails'
import {
  IncomingQuotationsController,
  incomingQuotationRequestIds,
} from 'features/RequestForQuotations/Quotation/IncomingQuotationsController'
import { outgoingRfqRequestIds } from 'features/RequestForQuotations/RFQ/OutgoingRfqsController'
import {
  canRetry,
  canSend,
  readableLabel,
} from 'features/RequestForQuotations/quotationExchange.utils'
import { BomItemType } from 'model/Project/BoMItemRow'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { useCallback, useEffect, useState } from 'react'
import {
  ChangeChannelRequest,
  DeliveryStatus,
  ProjectSupplierDto,
  IncomingQuotationState,
  IncomingQuotationSummaryDto,
  RequestForQuotationDto,
  OutgoingRequestForQuotationDto,
  RequestForQuotationState,
  RequestForQuotationSupplierRequestSummaryDto,
  RfqBoMItemReferenceDto,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import store from 'store/configureStore'
import { useShowException } from 'utils/useShowException'
import { ProjectRFQController } from '../ProjectRFQController'
import {
  matchesQuotationExchangeInvalidation,
  useQuotationExchangeInvalidation,
} from '../../useQuotationExchangeInvalidation'
import { useAppSelector } from 'store/configureStore'
import { IncomingQuotationDetails } from './IncomingQuotationDetails'

export type ManageRFQDialogProps = { isOpen: boolean; onClose: () => void }

type SupplierWithChannels = ProjectSupplierDto

const supplierLabel = (supplier: ProjectSupplierDto) =>
  supplier.name ||
  supplier.glNumber ||
  supplier.supplierId ||
  'Unnamed supplier'

const getWorkingSteps = (references?: RfqBoMItemReferenceDto[]) => {
  const steps = new Set<WorkingStepType>()
  references?.forEach((reference) =>
    reference.activities?.forEach((activity) => {
      if (activity.workingStepType) steps.add(activity.workingStepType)
    }),
  )
  return [...steps]
}

const formatMoney = (quotation: IncomingQuotationSummaryDto) => {
  const money = quotation.taxInclusiveAmount || quotation.taxExclusiveAmount
  if (money?.amount === undefined) return '-'
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: money.currency || 'EUR',
    }).format(money.amount)
  } catch {
    return `${money.amount} ${money.currency || ''}`.trim()
  }
}

function RfqScopeItemRow({ reference }: { reference: RfqBoMItemReferenceDto }) {
  const itemId = reference.buyerBoMItemId
  const project = store.getState().project
  const bomItemPointer = {
    id: itemId,
    type:
      itemId && project?.assemblyHeaders?.[itemId]
        ? BomItemType.assemblyType
        : BomItemType.partType,
  } as BomItemPointer
  const { imageSrc } = useBomItemThumbnailImageSrc(bomItemPointer)
  const itemName = reference.buyerOriginalName || itemId || 'Unknown item'

  return (
    <TableRow>
      <TableCell>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              flexShrink: 0,
              borderRadius: 1,
              bgcolor: 'action.hover',
              display: 'grid',
              placeItems: 'center',
              overflow: 'hidden',
            }}
          >
            {imageSrc && (
              <Box
                component="img"
                src={imageSrc}
                alt={itemName}
                sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            )}
          </Box>
          <Typography variant="body2">{itemName}</Typography>
        </Stack>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
          {reference.activities?.map((activity, index) =>
            activity.workingStepType ? (
              <WorkingStepIconSvg
                key={
                  activity.buyerActivityId ||
                  `${activity.workingStepType}-${index}`
                }
                workingStepType={activity.workingStepType}
                attributes={{ width: 28, height: 28 }}
              />
            ) : null,
          )}
          {reference.activities?.some(
            (activity) => activity.requestedQuantityBreaks?.length,
          ) && (
            <Typography variant="caption" color="text.secondary">
              Requested tiers:{' '}
              {reference.activities
                .flatMap((activity) => activity.requestedQuantityBreaks ?? [])
                .filter((value, index, all) => all.indexOf(value) === index)
                .sort((a, b) => a - b)
                .join(', ')}
            </Typography>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  )
}

function ComparisonTable({
  quotations,
  awardedQuotationId,
  onComplete,
}: {
  quotations: IncomingQuotationSummaryDto[]
  awardedQuotationId?: string
  onComplete: () => Promise<void> | void
}) {
  const { controller, loading } = useAppController(
    () => new IncomingQuotationsController(),
  )
  const showException = useShowException('quotations')

  const decide = async (
    quotation: IncomingQuotationSummaryDto,
    accept: boolean,
  ) => {
    if (!quotation.id) return
    try {
      if (accept) await controller.accept(quotation.id)
      else await controller.reject(quotation.id)
      await onComplete()
    } catch (error) {
      showException(error)
    }
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
        Compare quotations
      </Typography>
      <Table size="small" aria-label="Quotation comparison">
        <TableHead>
          <TableRow>
            <TableCell>Supplier</TableCell>
            <TableCell align="right">Total price</TableCell>
            <TableCell align="right">Decision</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {quotations.map((quotation) => {
            const awarded = quotation.id === awardedQuotationId
            const pending = quotation.state === IncomingQuotationState.Received
            const busy = Boolean(
              loading[incomingQuotationRequestIds.accept(quotation.id)] ||
              loading[incomingQuotationRequestIds.reject(quotation.id)],
            )
            return (
              <TableRow key={quotation.id}>
                <TableCell>
                  {quotation.supplierName || 'Unknown supplier'}
                  {awarded && (
                    <Chip
                      label="Awarded"
                      color="success"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </TableCell>
                <TableCell align="right">{formatMoney(quotation)}</TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    color="error"
                    disabled={!pending || busy}
                    onClick={() => decide(quotation, false)}
                  >
                    Reject
                  </Button>
                  <Button
                    size="small"
                    color="success"
                    disabled={!pending || busy || Boolean(awardedQuotationId)}
                    onClick={() => decide(quotation, true)}
                  >
                    Accept
                  </Button>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
}

function SupplierQuotation({
  supplierRequest,
  supplier,
  bomItemReferences,
  awardedQuotationId,
  controller,
  loading,
  onComplete,
}: {
  supplierRequest: RequestForQuotationSupplierRequestSummaryDto
  supplier?: ProjectSupplierDto
  bomItemReferences?: RfqBoMItemReferenceDto[]
  awardedQuotationId?: string
  controller: ProjectRFQController
  loading: Record<string, boolean>
  onComplete: () => Promise<void>
}) {
  const [channelOpen, setChannelOpen] = useState(false)
  const [copyingLink, setCopyingLink] = useState(false)
  const [linkCopied, setLinkCopied] = useState(false)
  const showException = useShowException('supplier invitation')

  const run = async (action: () => Promise<unknown>) => {
    try {
      await action()
      await onComplete()
    } catch (error) {
      showException(error)
    }
  }

  const deliveryId = supplierRequest.id || ''
  const sending = Boolean(loading[outgoingRfqRequestIds.send(deliveryId)])
  const retrying = Boolean(loading[outgoingRfqRequestIds.retry(deliveryId)])
  const changingChannel = Boolean(
    loading[outgoingRfqRequestIds.changeChannel(deliveryId)],
  )
  const downloading = Boolean(
    loading[outgoingRfqRequestIds.download(deliveryId)],
  )
  const busy = sending || retrying || changingChannel
  const changeChannel = (request: ChangeChannelRequest) =>
    run(() => controller.changeDeliveryChannel(deliveryId, request))
  const incomingQuotationId =
    supplierRequest.incomingQuotationId || supplierRequest.quotation?.id
  const invitationScope = supplierRequest.boMItemReferences ?? bomItemReferences
  const invitationActivities =
    invitationScope?.flatMap((reference, referenceIndex) =>
      (reference.activities ?? []).map((activity) => ({
        ...activity,
        itemKey: reference.buyerBoMItemId || `item-${referenceIndex}`,
      })),
    ) ?? []
  const invitationActivityGroups = new Map<
    WorkingStepType | undefined,
    Map<string, number | undefined>
  >()
  invitationActivities.forEach((activity) => {
    const itemQuantities =
      invitationActivityGroups.get(activity.workingStepType) ?? new Map()
    if (!itemQuantities.has(activity.itemKey)) {
      itemQuantities.set(
        activity.itemKey,
        activity.requestedQuantityBreaks?.[0],
      )
    }
    invitationActivityGroups.set(activity.workingStepType, itemQuantities)
  })

  const copyResponseLink = async () => {
    if (!deliveryId) return
    setCopyingLink(true)
    try {
      const link = await controller.createResponseLink(deliveryId)
      await navigator.clipboard.writeText(link.url)
      setLinkCopied(true)
      window.setTimeout(() => setLinkCopied(false), 2500)
    } catch (error) {
      showException(error)
    } finally {
      setCopyingLink(false)
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ justifyContent: 'space-between' }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {supplierRequest.recipient?.displayName ||
              (supplier && supplierLabel(supplier)) ||
              'Unknown supplier'}
          </Typography>
          {supplier?.glNumber && (
            <Typography variant="caption" color="text.secondary">
              GL number: {supplier.glNumber}
            </Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
            <ChannelBadge channel={supplierRequest.channel} />
            <Chip
              size="small"
              label={readableLabel(supplierRequest.deliveryStatus)}
              variant="outlined"
            />
            {awardedQuotationId &&
              supplierRequest.quotation?.id === awardedQuotationId && (
                <Chip size="small" color="success" label="Awarded" />
              )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Button
            size="small"
            startIcon={
              copyingLink ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <ContentCopyOutlined />
              )
            }
            disabled={!deliveryId || copyingLink}
            onClick={copyResponseLink}
          >
            {linkCopied ? 'Link copied' : 'Copy response link'}
          </Button>
          <Button
            size="small"
            startIcon={
              changingChannel ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SwapHorizOutlined />
              )
            }
            disabled={!supplierRequest.id || busy}
            onClick={() => setChannelOpen(true)}
          >
            Select channel
          </Button>
          {((canSend(supplierRequest.deliveryStatus) &&
            supplierRequest.deliveryStatus !== DeliveryStatus.Sent) ||
            supplierRequest.deliveryStatus === undefined) && (
            <Button
              size="small"
              variant="contained"
              startIcon={
                sending || retrying ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SendOutlined />
                )
              }
              disabled={!supplierRequest.id || busy}
              onClick={() =>
                run(() =>
                  canRetry(supplierRequest.deliveryStatus)
                    ? controller.retryDelivery(deliveryId)
                    : controller.sendDelivery(deliveryId),
                )
              }
            >
              {canRetry(supplierRequest.deliveryStatus)
                ? 'Retry sending'
                : 'Send RFQ'}
            </Button>
          )}
          {supplierRequest.documentDownloadAvailable && (
            <Button
              size="small"
              startIcon={
                downloading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DownloadOutlined />
                )
              }
              disabled={downloading}
              onClick={() => controller.downloadDelivery(deliveryId)}
            >
              Download
            </Button>
          )}
        </Stack>
      </Stack>
      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          Supplier scope · {invitationActivityGroups.size} activit
          {invitationActivityGroups.size === 1 ? 'y' : 'ies'} ·{' '}
          {invitationScope?.length ?? 0} item
          {invitationScope?.length === 1 ? '' : 's'}
        </Typography>
        <Stack direction="row" sx={{ mt: 0.5, gap: 0.75, flexWrap: 'wrap' }}>
          {[...invitationActivityGroups.entries()].map(
            ([workingStepType, itemQuantities]) => {
              const quantities = [...itemQuantities.values()]
              const hasCompleteQuantity = quantities.every(
                (quantity) => quantity !== undefined,
              )
              const totalQuantity = quantities.reduce<number>(
                (total, quantity) => total + (quantity ?? 0),
                0,
              )
              const itemCount = itemQuantities.size

              return (
                <Chip
                  key={workingStepType ?? 'unknown'}
                  size="small"
                  variant="outlined"
                  label={`${workingStepType || 'Unknown activity'} · ${itemCount} item${itemCount === 1 ? '' : 's'}${
                    hasCompleteQuantity ? ` · ${totalQuantity} pcs` : ''
                  }`}
                />
              )
            },
          )}
        </Stack>
      </Box>
      <Box sx={{ mt: 1.5 }}>
        <ExchangeDeliveryDetails
          record={
            {
              ...supplierRequest,
              selectedChannel: supplierRequest.channel,
            } as OutgoingRequestForQuotationDto
          }
          onRetry={
            supplierRequest.deliveryStatus === DeliveryStatus.Sent
              ? () => run(() => controller.retryDelivery(deliveryId))
              : undefined
          }
          retrying={retrying}
          retryDisabled={!supplierRequest.id || busy}
        />
      </Box>
      {incomingQuotationId ? (
        <IncomingQuotationDetails
          incomingQuotationId={incomingQuotationId}
          bomItemReferences={bomItemReferences}
          onActionComplete={onComplete}
          competingAwarded={Boolean(
            awardedQuotationId &&
            supplierRequest.quotation?.id !== awardedQuotationId,
          )}
        />
      ) : supplierRequest.quotation ? (
        <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: 'center' }}>
          <Typography variant="body2">
            Quotation:{' '}
            {supplierRequest.quotation.supplierName || 'Unknown supplier'}
          </Typography>
          <Typography variant="body2">
            {formatMoney(supplierRequest.quotation)}
          </Typography>
          <Chip
            size="small"
            label={supplierRequest.quotation.state || 'Received'}
          />
        </Stack>
      ) : null}
      <ExchangeChannelDialog
        open={channelOpen}
        title="RFQ channel"
        channel={supplierRequest.channel}
        sender={supplierRequest.sender}
        recipient={supplierRequest.recipient}
        recipientOptions={supplier?.configuredEndpoints}
        onClose={() => setChannelOpen(false)}
        onSave={changeChannel}
      />
    </Paper>
  )
}

function CanonicalRfqCard({
  rfq,
  suppliers,
  controller,
  loading,
  onRefresh,
  onDelete,
}: {
  rfq: RequestForQuotationDto
  suppliers: SupplierWithChannels[]
  controller: ProjectRFQController
  loading: Record<string, boolean>
  onRefresh: () => Promise<void>
  onDelete: () => void
}) {
  const getWorkingStepName = useLocalizedWorkingStepLabel()
  const [scopeExpanded, setScopeExpanded] = useState(false)
  const quotations = (rfq.supplierRequests ?? []).flatMap((supplierRequest) =>
    supplierRequest.quotation ? [supplierRequest.quotation] : [],
  )
  const hasSentDelivery = rfq.supplierRequests?.some(
    (supplierRequest) =>
      supplierRequest.deliveryStatus === DeliveryStatus.Sent ||
      supplierRequest.deliveryStatus === DeliveryStatus.Delivered,
  )
  const awardedQuotation = hasSentDelivery
    ? quotations.find(
        (quotation) => quotation.state === IncomingQuotationState.Accepted,
      )
    : undefined
  const awardedQuotationId = awardedQuotation?.id
  const displayState = awardedQuotation
    ? RequestForQuotationState.Awarded
    : rfq.state === RequestForQuotationState.Awarded && !hasSentDelivery
      ? (rfq.supplierRequests?.length ?? 0) > 0
        ? RequestForQuotationState.Open
        : RequestForQuotationState.Draft
      : rfq.state
  const workingSteps = getWorkingSteps(rfq.boMItemReferences)
  const itemCount = rfq.boMItemReferences?.length ?? 0
  const activityCount =
    rfq.boMItemReferences?.reduce(
      (count, reference) => count + (reference.activities?.length ?? 0),
      0,
    ) ?? 0
  const activityDescription = workingSteps.map(getWorkingStepName).join(' | ')
  const description = `${activityDescription || 'No activities'} - (${itemCount} item${itemCount === 1 ? '' : 's'})`

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ justifyContent: 'space-between' }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {rfq.title || description}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {rfq.buyerReference || 'No buyer reference'} · {activityCount}{' '}
            activit{activityCount === 1 ? 'y' : 'ies'} ·{' '}
            {rfq.supplierRequests?.length ?? 0} supplier invitation
            {(rfq.supplierRequests?.length ?? 0) === 1 ? '' : 's'}
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 0.75 }}>
            <Typography variant="body2" color="text.secondary">
              <CalendarTodayOutlined sx={{ fontSize: 15, mr: 0.5 }} />
              {rfq.dateCreated
                ? new Date(rfq.dateCreated).toLocaleString()
                : 'Unknown date'}
            </Typography>
            {rfq.responseDeadline &&
              new Date(rfq.responseDeadline).getFullYear() > 2000 && (
                <Typography variant="body2" color="text.secondary">
                  Responses due{' '}
                  {new Date(rfq.responseDeadline).toLocaleDateString()}
                </Typography>
              )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
          <Chip label={displayState || 'Unknown'} variant="outlined" />
          <IconButton aria-label="Delete RFQ" size="small" onClick={onDelete}>
            <DeleteOutlined />
          </IconButton>
        </Stack>
      </Stack>

      <Button
        size="small"
        startIcon={<ExpandMoreOutlined />}
        onClick={() => setScopeExpanded((value) => !value)}
        sx={{ mt: 0.5 }}
      >
        {scopeExpanded ? 'Hide RFQ scope' : 'Show RFQ scope'}
      </Button>
      <Collapse in={scopeExpanded}>
        <Table size="small" aria-label="RFQ scope">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Activities</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rfq.boMItemReferences?.map((reference) => (
              <RfqScopeItemRow
                key={reference.buyerBoMItemId}
                reference={reference}
              />
            ))}
          </TableBody>
        </Table>
      </Collapse>

      {rfq.state === RequestForQuotationState.Draft &&
        (rfq.supplierRequests?.length ?? 0) === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            This draft has no supplier invitations. Open a new RFQ draft to
            assign exact activities and publish invitations together.
          </Alert>
        )}

      {(rfq.supplierRequests?.length ?? 0) > 0 && (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {rfq.supplierRequests?.map((supplierRequest) => (
            <SupplierQuotation
              key={supplierRequest.id}
              supplierRequest={supplierRequest}
              supplier={suppliers.find(
                (supplier) =>
                  supplier.supplierId === supplierRequest.supplierId,
              )}
              bomItemReferences={
                supplierRequest.boMItemReferences ?? rfq.boMItemReferences
              }
              awardedQuotationId={awardedQuotationId}
              controller={controller}
              loading={loading}
              onComplete={onRefresh}
            />
          ))}
        </Stack>
      )}

      {quotations.length > 1 && (
        <Box sx={{ mt: 2 }}>
          <ComparisonTable
            quotations={quotations}
            awardedQuotationId={awardedQuotationId}
            onComplete={onRefresh}
          />
        </Box>
      )}
    </Paper>
  )
}

export const ManageRFQDialog = ({ isOpen, onClose }: ManageRFQDialogProps) => {
  const projectId = useAppSelector((state) => state.project.activeProject?.id)
  const invalidation = useQuotationExchangeInvalidation()
  const { controller, loading } = useAppController(
    () => new ProjectRFQController(),
  )
  const { controller: suppliersController } = useAppController(
    () => new ProjectSuppliersController(),
  )
  const { openDialog, closeDialog } = useDialogManager()
  const showException = useShowException('project RFQs')
  const [rfqs, setRfqs] = useState<RequestForQuotationDto[]>([])
  const [suppliers, setSuppliers] = useState<SupplierWithChannels[]>([])
  const [initialLoading, setInitialLoading] = useState(false)

  const loadRfqs = useCallback(async () => {
    setInitialLoading(true)
    try {
      setRfqs(await controller.getProjectRFQs())
    } catch (error) {
      showException(error)
    } finally {
      setInitialLoading(false)
    }
  }, [controller, showException])

  const loadSuppliers = useCallback(async () => {
    try {
      const response = await suppliersController.getSuppliers()
      setSuppliers((response.projectSuppliers ?? []) as SupplierWithChannels[])
    } catch (error) {
      showException(error)
    }
  }, [showException, suppliersController])

  useEffect(() => {
    if (isOpen) void Promise.all([loadRfqs(), loadSuppliers()])
  }, [isOpen, loadRfqs, loadSuppliers])

  useEffect(() => {
    if (
      isOpen &&
      invalidation.sequence &&
      matchesQuotationExchangeInvalidation(invalidation.payload, { projectId })
    ) {
      void loadRfqs()
    }
  }, [invalidation, isOpen, loadRfqs, projectId])

  const deleteRfq = (rfq: RequestForQuotationDto) => {
    if (!rfq.id) return
    openDialog('ConfirmationDialog', {
      title: 'Delete RFQ',
      message: 'Delete this RFQ and all of its supplier requests?',
      confirmText: 'Delete',
      disableAutoCloseOnConfirm: true,
      onConfirm: async () => {
        await controller.deleteCanonical(rfq.id!)
        await loadRfqs()
        closeDialog()
      },
    })
  }

  return (
    <Dialog
      open={isOpen}
      onClose={(_, reason) => {
        if (reason !== 'escapeKeyDown') onClose()
      }}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="h6">Manage project RFQs</Typography>
          <Chip label={rfqs.length} size="small" />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton
              aria-label="Refresh RFQs"
              disabled={loading.ListRequestForQuotations}
              onClick={loadRfqs}
            >
              {loading.ListRequestForQuotations ? (
                <CircularProgress size={18} />
              ) : (
                <RefreshOutlined />
              )}
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlined />}
            onClick={() => openDialog('DraftRFQDialog')}
          >
            New RFQ
          </Button>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {initialLoading ? (
          <Box sx={{ py: 6, display: 'grid', placeItems: 'center' }}>
            <CircularProgress aria-label="Loading RFQs" />
          </Box>
        ) : rfqs.length === 0 ? (
          <Typography
            color="text.secondary"
            sx={{ py: 6, textAlign: 'center' }}
          >
            No RFQs found
          </Typography>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            {rfqs.map((rfq) => (
              <CanonicalRfqCard
                key={rfq.id}
                rfq={rfq}
                suppliers={suppliers}
                controller={controller}
                loading={loading}
                onRefresh={loadRfqs}
                onDelete={() => deleteRfq(rfq)}
              />
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
